import React, { useEffect, useMemo, useState } from "react";
import {
  Button as AntButton,
  Checkbox,
  DatePicker,
  Input,
  InputNumber,
  Radio,
  Select,
  TimePicker,
  Tooltip,
} from "antd";
import {
  CalendarOutlined,
  DeleteOutlined,
  DownloadOutlined,
  PlusOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import dayjs, { Dayjs } from "dayjs";

import FieldLabel from "../../../../projects/components/FieldLabel";
import { notify } from "../../../../../services/toast.config";
import { storedToExceptions } from "../helpers/payrollTemplate.helper";

import type {
  AppliesToType,
  ExceptionAppliesTo,
  ExceptionEntry,
  ExtensivePayrollPayload,
  RawFormState,
  RuleCode,
  TemplateConfig,
} from "../types";

interface PayrollReportFormProps {
  selectedCompany: any;
  departments: any[];
  employees: any[];
  loading: boolean;
  prefill: { config: TemplateConfig; key: number } | null;
  isUnrestricted: boolean;
  lockedDepartmentId: number | null;
  onDownload: (payload: ExtensivePayrollPayload) => Promise<void>;
  onSaveTemplateClick: (state: RawFormState) => void;
}

const RULE_OPTIONS: { code: RuleCode; label: string }[] = [
  { code: "late_bypass", label: "Bypass Late" },
  { code: "early_departure_bypass", label: "Bypass Early Departure" },
  { code: "treat_as_holiday", label: "Treat as Holiday" },
  { code: "ignore_sl_hd", label: "Ignore SL / HD" },
  { code: "wfh", label: "Work From Home" },
];

const WEEK_DAY_OPTIONS = [
  { value: 0, label: "Sun" },
  { value: 1, label: "Mon" },
  { value: 2, label: "Tue" },
  { value: 3, label: "Wed" },
  { value: 4, label: "Thu" },
  { value: 5, label: "Fri" },
  { value: 6, label: "Sat" },
];

const SECTION_CARD_STYLE: React.CSSProperties = {
  background: "#f8fafc",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: 14,
};

const EXCEPTION_ROW_STYLE: React.CSSProperties = {
  background: "#ffffff",
  border: "1px solid #e2e8f0",
  borderRadius: 10,
  padding: 12,
  marginBottom: 10,
};

export const PayrollReportForm: React.FC<PayrollReportFormProps> = ({
  selectedCompany,
  departments,
  employees,
  loading,
  prefill,
  isUnrestricted,
  lockedDepartmentId,
  onDownload,
  onSaveTemplateClick,
}) => {
  const departmentLocked = !isUnrestricted && lockedDepartmentId != null;
  const [month, setMonth] = useState<Dayjs>(dayjs());
  const [deptIds, setDeptIds] = useState<number[]>([]);
  const [empIds, setEmpIds] = useState<number[]>([]);
  const [lateCutoff, setLateCutoff] = useState<Dayjs>(dayjs("09:45", "HH:mm"));
  const [weekOffDays, setWeekOffDays] = useState<number[]>([0]);
  const [lateForgiveness, setLateForgiveness] = useState<number>(2);
  const [exceptions, setExceptions] = useState<ExceptionEntry[]>([]);

  const resetState = () => {
    setDeptIds(departmentLocked ? [lockedDepartmentId as number] : []);
    setEmpIds([]);
    setLateCutoff(dayjs("09:45", "HH:mm"));
    setWeekOffDays([0]);
    setLateForgiveness(2);
    setExceptions([]);
  };

  useEffect(() => {
    resetState();
    setMonth(dayjs());
  }, [selectedCompany?.value, selectedCompany?.type]);

  useEffect(() => {
    if (departmentLocked) setDeptIds([lockedDepartmentId as number]);
  }, [departmentLocked, lockedDepartmentId]);

  useEffect(() => {
    if (!prefill) return;
    const c = prefill.config;
    const allowedEmployeeIds = new Set(
      (Array.isArray(employees) ? employees : []).map((e: any) => Number(e.id)),
    );
    setDeptIds(
      departmentLocked
        ? [lockedDepartmentId as number]
        : c.department_ids || [],
    );
    setEmpIds(
      departmentLocked
        ? (c.employee_ids || []).filter((id) => allowedEmployeeIds.has(Number(id)))
        : c.employee_ids || [],
    );
    setLateCutoff(
      c.late_cutoff_time ? dayjs(c.late_cutoff_time, "HH:mm") : dayjs("09:45", "HH:mm"),
    );
    setWeekOffDays(c.week_off_days?.length ? c.week_off_days : [0]);
    setLateForgiveness(c.late_forgiveness_count ?? 2);
    setExceptions(storedToExceptions(c.exceptions, month));
  }, [prefill]);

  const departmentOptions = useMemo(
    () =>
      Array.isArray(departments)
        ? departments.map((d: any) => ({
            value: Number(d.value),
            label: d.label,
          }))
        : [],
    [departments],
  );

  const employeeOptions = useMemo(() => {
    const list = Array.isArray(employees) ? employees : [];
    const filtered =
      deptIds.length > 0
        ? list.filter((emp: any) => deptIds.includes(Number(emp.department)))
        : list;
    return filtered.map((emp: any) => ({
      value: Number(emp.id),
      label:
        `${emp.fname || ""} ${emp.lname || ""}`.trim() ||
        emp.username ||
        `Employee-${emp.id}`,
    }));
  }, [employees, deptIds]);

  const allEmployeeOptions = useMemo(
    () =>
      (Array.isArray(employees) ? employees : []).map((emp: any) => ({
        value: Number(emp.id),
        label:
          `${emp.fname || ""} ${emp.lname || ""}`.trim() ||
          emp.username ||
          `Employee-${emp.id}`,
      })),
    [employees],
  );

  const monthBounds = useMemo(() => {
    const start = month.startOf("month");
    const end = month.endOf("month");
    return { start, end };
  }, [month]);

  const handleMonthChange = (v: Dayjs) => {
    setMonth(v);
    const dim = v.daysInMonth();
    const rebase = (d: Dayjs | null) =>
      d ? v.date(Math.min(d.date(), dim)) : null;
    setExceptions((prev) =>
      prev.map((e) => ({
        ...e,
        range: [rebase(e.range[0]), rebase(e.range[1])] as [
          Dayjs | null,
          Dayjs | null,
        ],
      })),
    );
  };

  const handleAddException = () => {
    setExceptions((prev) => [
      ...prev,
      {
        range: [null, null],
        weekdays: [],
        rules: new Set(),
        remarks: "",
        appliesTo: { type: "all", departmentIds: [], employeeIds: [] },
      },
    ]);
  };

  const handleExceptionRemarks = (index: number, value: string) => {
    setExceptions((prev) =>
      prev.map((e, i) => (i === index ? { ...e, remarks: value } : e)),
    );
  };

  const handleRemoveException = (index: number) => {
    setExceptions((prev) => prev.filter((_, i) => i !== index));
  };

  const handleExceptionRange = (
    index: number,
    value: [Dayjs | null, Dayjs | null] | null,
  ) => {
    const next: [Dayjs | null, Dayjs | null] = value
      ? [value[0] || null, value[1] || null]
      : [null, null];
    setExceptions((prev) =>
      prev.map((e, i) => (i === index ? { ...e, range: next } : e)),
    );
  };

  const handleExceptionWeekdays = (index: number, value: number[]) => {
    setExceptions((prev) =>
      prev.map((e, i) => (i === index ? { ...e, weekdays: value } : e)),
    );
  };

  const handleApplyType = (index: number, type: AppliesToType) => {
    setExceptions((prev) =>
      prev.map((e, i) =>
        i === index ? { ...e, appliesTo: { ...e.appliesTo, type } } : e,
      ),
    );
  };

  const handleApplyDepts = (index: number, ids: number[]) => {
    setExceptions((prev) =>
      prev.map((e, i) =>
        i === index
          ? { ...e, appliesTo: { ...e.appliesTo, departmentIds: ids } }
          : e,
      ),
    );
  };

  const handleApplyEmps = (index: number, ids: number[]) => {
    setExceptions((prev) =>
      prev.map((e, i) =>
        i === index
          ? { ...e, appliesTo: { ...e.appliesTo, employeeIds: ids } }
          : e,
      ),
    );
  };

  const handleToggleRule = (index: number, code: RuleCode) => {
    setExceptions((prev) =>
      prev.map((e, i) => {
        if (i !== index) return e;
        const next = new Set(e.rules);
        if (next.has(code)) next.delete(code);
        else next.add(code);
        return { ...e, rules: next };
      }),
    );
  };

  const isExceptionComplete = (e: ExceptionEntry) =>
    Boolean(e.range[0]) &&
    e.rules.size > 0 &&
    !(
      e.appliesTo.type === "department" &&
      e.appliesTo.departmentIds.length === 0
    ) &&
    !(
      e.appliesTo.type === "employees" && e.appliesTo.employeeIds.length === 0
    );

  const handleReset = () => {
    resetState();
    setMonth(dayjs());
  };

  const currentRawState = (): RawFormState => ({
    month,
    deptIds,
    empIds,
    lateCutoff,
    weekOffDays,
    lateForgiveness,
    exceptions,
  });

  const handleDownload = async () => {
    const partialTarget = exceptions.find(
      (ex) =>
        ex.range[0] &&
        ex.rules.size > 0 &&
        ((ex.appliesTo.type === "department" &&
          ex.appliesTo.departmentIds.length === 0) ||
          (ex.appliesTo.type === "employees" &&
            ex.appliesTo.employeeIds.length === 0)),
    );
    if (partialTarget) {
      notify.error(
        "An exception targets a department / specific employees but none are selected. Pick targets or set it to All Employees.",
      );
      return;
    }

    const expanded: ExtensivePayrollPayload["exceptions"] = [];
    for (const ex of exceptions) {
      const [start, end] = ex.range;
      if (!start || ex.rules.size === 0) continue;
      const rangeEnd = end || start;
      const startFmt = start.format("YYYY-MM-DD");
      const endFmt = rangeEnd.format("YYYY-MM-DD");
      const [from, to] =
        startFmt <= endFmt ? [start, rangeEnd] : [rangeEnd, start];
      let cur = from.clone();
      const stopFmt = to.format("YYYY-MM-DD");
      const trimmedRemarks = (ex.remarks || "").trim();
      const appliesTo: ExceptionAppliesTo = {
        type: ex.appliesTo.type,
        department_ids:
          ex.appliesTo.type === "department" ? ex.appliesTo.departmentIds : [],
        employee_ids:
          ex.appliesTo.type === "employees" ? ex.appliesTo.employeeIds : [],
      };
      while (cur.format("YYYY-MM-DD") <= stopFmt) {
        if (ex.weekdays.length === 0 || ex.weekdays.includes(cur.day())) {
          expanded.push({
            date: cur.format("YYYY-MM-DD"),
            rules: Array.from(ex.rules),
            ...(trimmedRemarks ? { remarks: trimmedRemarks } : {}),
            applies_to: appliesTo,
          });
        }
        cur = cur.add(1, "day");
      }
    }

    const payload: ExtensivePayrollPayload = {
      month: month.format("YYYY-MM"),
      department_ids: deptIds,
      employee_ids: empIds,
      late_cutoff_time: lateCutoff ? lateCutoff.format("HH:mm") : "09:45",
      week_off_days: weekOffDays.length ? weekOffDays : [0],
      late_forgiveness_count: Math.max(0, Number(lateForgiveness) || 0),
      exceptions: expanded,
    };
    await onDownload(payload);
  };

  const isDisabled = loading || !selectedCompany || !month;
  const configuredExceptionCount = exceptions.filter(isExceptionComplete).length;
  const targetedExceptionCount = exceptions.filter(
    (e) => isExceptionComplete(e) && e.appliesTo.type !== "all",
  ).length;

  return (
    <div className="d-flex flex-column" style={{ gap: 16 }}>
      <div style={SECTION_CARD_STYLE}>
        <div className="row g-3">
          <div className="col-md-6">
            <FieldLabel required>Month</FieldLabel>
            <DatePicker
              picker="month"
              size="large"
              style={{ width: "100%" }}
              value={month}
              onChange={(v) => v && handleMonthChange(v)}
              format="MMMM YYYY"
              suffixIcon={<CalendarOutlined />}
              allowClear={false}
            />
          </div>
          <div className="col-md-3">
            <FieldLabel>Late Cutoff</FieldLabel>
            <TimePicker
              size="large"
              format="HH:mm"
              style={{ width: "100%" }}
              value={lateCutoff}
              onChange={(v) => v && setLateCutoff(v)}
              minuteStep={5}
              allowClear={false}
            />
            <div
              className="small mt-1"
              style={{ color: "#94a3b8", fontSize: 11.5 }}
            >
              Per-report only.
            </div>
          </div>
          <div className="col-md-3">
            <FieldLabel>Forgive Late Count</FieldLabel>
            <InputNumber
              size="large"
              min={0}
              max={31}
              style={{ width: "100%" }}
              value={lateForgiveness}
              onChange={(v) => setLateForgiveness(Number(v) || 0)}
            />
            <div
              className="small mt-1"
              style={{ color: "#94a3b8", fontSize: 11.5 }}
            >
              First N lates ignored per employee.
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-12">
            <FieldLabel>Week Off Days</FieldLabel>
            <Select
              mode="multiple"
              size="large"
              style={{ width: "100%" }}
              placeholder="Select week-off days"
              value={weekOffDays}
              onChange={(v) => setWeekOffDays(v as number[])}
              options={WEEK_DAY_OPTIONS}
              allowClear={false}
            />
            <div
              className="small mt-1"
              style={{ color: "#94a3b8", fontSize: 11.5 }}
            >
              Default Sunday. Days listed here are treated as week-off in the
              report.
            </div>
          </div>
        </div>

        <div className="row g-3 mt-1">
          <div className="col-md-6">
            <FieldLabel>Departments</FieldLabel>
            <Select
              mode="multiple"
              size="large"
              style={{ width: "100%" }}
              placeholder={departmentLocked ? "Your department" : "All departments"}
              value={deptIds}
              onChange={(v) => setDeptIds(v as number[])}
              options={departmentOptions}
              maxTagCount="responsive"
              allowClear={!departmentLocked}
              disabled={departmentLocked}
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div className="col-md-6">
            <FieldLabel>Employees</FieldLabel>
            <Select
              mode="multiple"
              size="large"
              style={{ width: "100%" }}
              placeholder={
                deptIds.length
                  ? "All employees in selected departments"
                  : "All employees"
              }
              value={empIds}
              onChange={(v) => setEmpIds(v as number[])}
              options={employeeOptions}
              maxTagCount="responsive"
              allowClear
              showSearch
              optionFilterProp="label"
            />
          </div>
          <div className="col-12">
            <div
              className="small mt-1"
              style={{ color: "#94a3b8", fontSize: 11.5 }}
            >
              These define who appears in the report. Each exception below has
              its own "Applies To" that narrows which of these employees it
              affects.
            </div>
          </div>
        </div>
      </div>

      <div>
        <div
          className="d-flex justify-content-between align-items-center mb-2"
          style={{ paddingInline: 2 }}
        >
          <div>
            <FieldLabel>Exception Days</FieldLabel>
            <div className="small" style={{ color: "#94a3b8", fontSize: 11.5 }}>
              Flag dates where late / early-out / SL-HD rules should be relaxed.
              Absent days still count unless "Treat as Holiday" is set.
            </div>
          </div>
          <AntButton
            size="small"
            icon={<PlusOutlined />}
            onClick={handleAddException}
          >
            Add Exception
          </AntButton>
        </div>

        {exceptions.length === 0 && (
          <div
            style={{
              ...EXCEPTION_ROW_STYLE,
              background: "#f8fafc",
              color: "#94a3b8",
              fontSize: 12.5,
              textAlign: "center",
            }}
          >
            No exception days configured.
          </div>
        )}

        {exceptions.map((ex, idx) => (
          <div key={idx} style={EXCEPTION_ROW_STYLE}>
            <div className="row g-2 align-items-start">
              <div className="col-md-5">
                <DatePicker.RangePicker
                  size="middle"
                  style={{ width: "100%" }}
                  value={ex.range as any}
                  onChange={(v) =>
                    handleExceptionRange(
                      idx,
                      v as [Dayjs | null, Dayjs | null] | null,
                    )
                  }
                  disabledDate={(current) =>
                    !current ||
                    current.isBefore(monthBounds.start, "day") ||
                    current.isAfter(monthBounds.end, "day")
                  }
                  defaultPickerValue={[monthBounds.start, monthBounds.start]}
                  format="YYYY-MM-DD"
                  placeholder={["From date", "To date"]}
                  allowEmpty={[false, true]}
                />
              </div>
              <div className="col-md-6">
                <div className="d-flex flex-wrap" style={{ gap: "6px 16px" }}>
                  {RULE_OPTIONS.map((opt) => (
                    <Checkbox
                      key={opt.code}
                      checked={ex.rules.has(opt.code)}
                      onChange={() => handleToggleRule(idx, opt.code)}
                    >
                      <span style={{ fontSize: 13 }}>{opt.label}</span>
                    </Checkbox>
                  ))}
                </div>
              </div>
              <div
                className="col-md-1 d-flex justify-content-end"
                style={{ paddingTop: 2 }}
              >
                <Tooltip title="Remove exception">
                  <AntButton
                    size="small"
                    danger
                    type="text"
                    icon={<DeleteOutlined />}
                    onClick={() => handleRemoveException(idx)}
                  />
                </Tooltip>
              </div>
            </div>
            <div className="row g-2 mt-2 align-items-start">
              <div className="col-md-5">
                <div
                  className="small mb-1"
                  style={{ color: "#64748b", fontSize: 11.5 }}
                >
                  Days of week
                </div>
                <Select
                  mode="multiple"
                  size="middle"
                  style={{ width: "100%" }}
                  placeholder="All days in range"
                  value={ex.weekdays}
                  onChange={(v) => handleExceptionWeekdays(idx, v as number[])}
                  options={WEEK_DAY_OPTIONS}
                  maxTagCount="responsive"
                  allowClear
                />
              </div>
              <div className="col-md-7">
                <div
                  className="small mb-1"
                  style={{ color: "#64748b", fontSize: 11.5 }}
                >
                  Applies to
                </div>
                <Radio.Group
                  size="middle"
                  optionType="button"
                  buttonStyle="solid"
                  value={ex.appliesTo.type}
                  onChange={(e) => handleApplyType(idx, e.target.value)}
                >
                  <Radio value="all">All Employees</Radio>
                  <Radio value="department">Department(s)</Radio>
                  <Radio value="employees">Specific Employees</Radio>
                </Radio.Group>
                {ex.appliesTo.type === "department" && (
                  <Select
                    mode="multiple"
                    size="middle"
                    style={{ width: "100%", marginTop: 8 }}
                    placeholder="Select department(s)"
                    value={ex.appliesTo.departmentIds}
                    onChange={(v) => handleApplyDepts(idx, v as number[])}
                    options={departmentOptions}
                    maxTagCount="responsive"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                  />
                )}
                {ex.appliesTo.type === "employees" && (
                  <Select
                    mode="multiple"
                    size="middle"
                    style={{ width: "100%", marginTop: 8 }}
                    placeholder="Select employee(s)"
                    value={ex.appliesTo.employeeIds}
                    onChange={(v) => handleApplyEmps(idx, v as number[])}
                    options={allEmployeeOptions}
                    maxTagCount="responsive"
                    allowClear
                    showSearch
                    optionFilterProp="label"
                  />
                )}
              </div>
            </div>
            <div className="row g-2 mt-2">
              <div className="col-12">
                <Input
                  size="middle"
                  placeholder="Remarks (optional, e.g. Holi Festival, Office closed)"
                  value={ex.remarks}
                  maxLength={500}
                  onChange={(e) => handleExceptionRemarks(idx, e.target.value)}
                />
              </div>
            </div>
          </div>
        ))}
      </div>

      <div
        style={{
          padding: "12px 16px",
          background: "#ecfeff",
          border: "1px solid #a5f3fc",
          borderRadius: 10,
          color: "#0e7490",
          fontSize: 12.5,
        }}
      >
        <div style={{ fontWeight: 600, marginBottom: 6 }}>Report Summary</div>
        <ul className="mb-0" style={{ paddingLeft: 18, lineHeight: 1.7 }}>
          <li>Month: {month.format("MMMM YYYY")}</li>
          <li>Departments: {deptIds.length ? `${deptIds.length} selected` : "All"}</li>
          <li>
            Employees: {empIds.length ? `${empIds.length} selected` : "All in scope"}
          </li>
          <li>
            Late cutoff: {lateCutoff.format("HH:mm")} · Forgive first{" "}
            {lateForgiveness} late {lateForgiveness === 1 ? "count" : "counts"}
          </li>
          <li>
            Week-off days:{" "}
            {weekOffDays.length
              ? weekOffDays
                  .map(
                    (d) =>
                      WEEK_DAY_OPTIONS.find((o) => o.value === d)?.label || d,
                  )
                  .join(", ")
              : "None"}
          </li>
          <li>
            Exception days: {configuredExceptionCount} configured
            {targetedExceptionCount
              ? ` (${targetedExceptionCount} targeted to specific departments / employees)`
              : ""}
          </li>
          <li>Format: Excel (.xlsx)</li>
        </ul>
      </div>

      <div
        className="d-flex justify-content-between align-items-center"
        style={{ gap: 8 }}
      >
        <AntButton onClick={handleReset} disabled={loading}>
          Reset
        </AntButton>
        <div style={{ display: "flex", gap: 8 }}>
          <AntButton
            icon={<SaveOutlined />}
            disabled={loading || !selectedCompany}
            onClick={() => onSaveTemplateClick(currentRawState())}
          >
            Save as Template
          </AntButton>
          <AntButton
            type="primary"
            icon={<DownloadOutlined />}
            loading={loading}
            disabled={isDisabled}
            onClick={handleDownload}
            style={{
              background: "#059669",
              borderColor: "#059669",
              fontWeight: 600,
            }}
          >
            {loading ? "Generating..." : "Download Report"}
          </AntButton>
        </div>
      </div>
    </div>
  );
};

export default PayrollReportForm;
